import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddRounded,
  DeleteOutlineRounded,
  EditOutlined,
  LockOutlined,
  PublicOutlined,
  GroupsOutlined,
  SchoolOutlined,
} from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../../components/ConfirmProvider';
import type { TFunction } from 'i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenantStore } from '../../../stores/tenant.store';
import { useStudentIdentity } from '../../../hooks/use-student-identity';
import {
  journalApi,
  type JournalEntryDto,
  type JournalVisibility,
} from '../../../lib/api/journal.api';
import { PortalEmpty, PortalLoading } from '../PortalShell';
import { RichSpiritualText } from '../scripture/RichSpiritualText';
import { errorText } from '../../../lib/error-text';
import { formatDate } from '../../../lib/i18n-format';

function visibilityLabel(v: JournalVisibility, t: TFunction): string {
  if (v === 'private') return t('portal.journal.visibilityPrivate');
  if (v === 'mentor') return t('portal.journal.visibilityMentor');
  if (v === 'faculty') return t('portal.journal.visibilityFaculty');
  return t('portal.journal.visibilitySeminary');
}

function visibilityIcon(v: JournalVisibility): JSX.Element {
  if (v === 'private') return <LockOutlined fontSize="small" />;
  if (v === 'mentor') return <GroupsOutlined fontSize="small" />;
  if (v === 'faculty') return <SchoolOutlined fontSize="small" />;
  return <PublicOutlined fontSize="small" />;
}

export function JournalPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const confirm = useConfirm();
  const { currentTenantId } = useTenantStore();
  const { personId } = useStudentIdentity();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<JournalEntryDto | 'new' | null>(null);

  const listQ = useQuery({
    queryKey: ['portal-v2-journal', currentTenantId, personId],
    enabled: !!currentTenantId,
    queryFn: () =>
      journalApi.listMine({
        tenantId: currentTenantId!,
        ...(personId ? { studentId: personId } : {}),
      }),
  });

  const remove = useMutation({
    mutationFn: (entryId: string) =>
      journalApi.delete({
        tenantId: currentTenantId!,
        entryId,
        ...(personId ? { studentId: personId } : {}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-v2-journal'] }),
  });

  if (listQ.isLoading) return <PortalLoading />;
  const items = listQ.data?.items ?? [];

  return (
    <Stack spacing={2.5}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'flex-end' }}
        justifyContent="space-between"
        spacing={1.5}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h4" fontWeight={700} color="#e6edf3">
            {t('portal.journal.title')}
          </Typography>
          <Typography variant="body2" color="#8b949e">
            {t('portal.journal.subtitle')}
          </Typography>
        </Box>
        <Button
          startIcon={<AddRounded />}
          variant="contained"
          onClick={() => setEditing('new')}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' }, flexShrink: 0 }}
        >
          {t('portal.journal.newEntry')}
        </Button>
      </Stack>

      {items.length === 0 && (
        <PortalEmpty
          icon={<LockOutlined fontSize="inherit" />}
          title={t('portal.journal.emptyTitle')}
          body={t('portal.journal.emptyBody')}
        />
      )}

      {items.map((e) => (
        <Card
          key={e.id}
          sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2, p: 2.5 }}
        >
          <Stack direction="row" alignItems="flex-start" spacing={1.5} mb={1.5}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={700} color="#e6edf3">
                {e.title}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip
                  size="small"
                  icon={visibilityIcon(e.visibility)}
                  label={visibilityLabel(e.visibility, t)}
                  variant="outlined"
                  sx={{ height: 20, fontSize: 11, borderColor: '#30363d' }}
                />
                <Typography variant="caption" color="#6e7681">
                  {formatDate(e.createdAt, {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </Typography>
                {e.tags.map((t) => (
                  <Chip
                    key={t}
                    size="small"
                    label={t}
                    sx={{
                      height: 18,
                      fontSize: 10,
                      bgcolor: 'rgba(99,102,241,0.15)',
                      color: '#a5b4fc',
                    }}
                  />
                ))}
              </Stack>
            </Box>
            <IconButton size="small" onClick={() => setEditing(e)} sx={{ color: '#8b949e' }}>
              <EditOutlined fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={async () => {
                if (
                  await confirm({
                    message: t('portal.journal.deleteConfirm'),
                    confirmColor: 'error',
                  })
                )
                  remove.mutate(e.id);
              }}
            >
              <DeleteOutlineRounded fontSize="small" />
            </IconButton>
          </Stack>
          <Typography variant="body2" color="#c9d1d9" sx={{ whiteSpace: 'pre-wrap' }}>
            <RichSpiritualText>{e.body}</RichSpiritualText>
          </Typography>
        </Card>
      ))}

      {editing && (
        <JournalEditor
          entry={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['portal-v2-journal'] });
          }}
        />
      )}
    </Stack>
  );
}

function JournalEditor({
  entry,
  onClose,
  onSaved,
}: {
  entry: JournalEntryDto | null;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const { personId } = useStudentIdentity();
  const [title, setTitle] = useState(entry?.title ?? '');
  const [body, setBody] = useState(entry?.body ?? '');
  const [visibility, setVisibility] = useState<JournalVisibility>(entry?.visibility ?? 'private');
  const [tagsCsv, setTagsCsv] = useState((entry?.tags ?? []).join(', '));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSave = title.trim().length > 0 && body.trim().length > 0;

  const save = async () => {
    if (!currentTenantId) return;
    setSaving(true);
    setError(null);
    const tags = tagsCsv
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    try {
      if (entry) {
        await journalApi.update({
          tenantId: currentTenantId,
          entryId: entry.id,
          patch: { title: title.trim(), body: body.trim(), visibility, tags },
          ...(personId ? { studentId: personId } : {}),
        });
      } else {
        await journalApi.create({
          tenantId: currentTenantId,
          title: title.trim(),
          body: body.trim(),
          visibility,
          tags,
          ...(personId ? { studentId: personId } : {}),
        });
      }
      onSaved();
    } catch (e) {
      setError(errorText(e, t('portal.journal.saveError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {entry ? t('portal.journal.editEntryTitle') : t('portal.journal.newEntryTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={t('portal.journal.fieldTitle')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label={t('portal.journal.fieldBody')}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            multiline
            minRows={8}
            fullWidth
            size="small"
            placeholder={t('portal.journal.fieldBodyPlaceholder')}
          />
          <TextField
            select
            label={t('portal.journal.fieldVisibility')}
            value={visibility}
            onChange={(e) => setVisibility(e.target.value as JournalVisibility)}
            fullWidth
            size="small"
          >
            <MenuItem value="private">{t('portal.journal.visibilityOptionPrivate')}</MenuItem>
            <MenuItem value="mentor">{t('portal.journal.visibilityOptionMentor')}</MenuItem>
            <MenuItem value="faculty">{t('portal.journal.visibilityOptionFaculty')}</MenuItem>
            <MenuItem value="seminary">{t('portal.journal.visibilityOptionSeminary')}</MenuItem>
          </TextField>
          <TextField
            label={t('portal.journal.fieldTags')}
            value={tagsCsv}
            onChange={(e) => setTagsCsv(e.target.value)}
            fullWidth
            size="small"
            placeholder={t('portal.journal.fieldTagsPlaceholder')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t('portal.journal.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={!canSave || saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {saving
            ? t('portal.journal.saving')
            : entry
              ? t('portal.journal.save')
              : t('portal.journal.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
